import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Check, Loader2, ChevronDown } from "lucide-react";
import { useTranslate } from "../context/TranslateContext";
import { LANGUAGES } from "../mock/data";

export const TranslateDropdown = ({ testId = "translate-dropdown" }) => {
  const { lang, changeLanguage, loading } = useTranslate();
  const [open, setOpen] = useState(false);

  const select = (l) => { changeLanguage(l); setOpen(false); };

  return (
    <div className="relative" data-testid={testId}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testId}-trigger`}
        className="inline-flex items-center gap-2 rounded-full border border-edge bg-surface/70 px-4 py-2 text-sm text-cream backdrop-blur-md transition-colors duration-300 hover:border-gold hover:text-gold"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin text-gold" /> : <Globe className="h-4 w-4 text-gold" />}
        <span className="font-body">{lang.native}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.2, ease: [0.76, 0, 0.24, 1] }}
              data-testid={`${testId}-menu`}
              className="absolute right-0 z-50 mt-3 max-h-[70vh] w-64 overflow-y-auto rounded-2xl border border-edge bg-surface p-2 shadow-2xl shadow-black/60"
            >
              <div className="px-3 py-2 text-[0.65rem] uppercase tracking-[0.25em] text-gold">Original</div>
              {LANGUAGES.filter((l) => l.code === "en").map((l) => (
                <LangRow key={l.code} l={l} active={lang.code === l.code} onClick={() => select(l)} testId={testId} />
              ))}
              <div className="mt-2 px-3 py-2 text-[0.65rem] uppercase tracking-[0.25em] text-gold">Indian Languages</div>
              {LANGUAGES.filter((l) => l.indian).map((l) => (
                <LangRow key={l.code} l={l} active={lang.code === l.code} onClick={() => select(l)} testId={testId} />
              ))}
              <div className="mt-2 px-3 py-2 text-[0.65rem] uppercase tracking-[0.25em] text-gold">International</div>
              {LANGUAGES.filter((l) => !l.indian && l.code !== "en").map((l) => (
                <LangRow key={l.code} l={l} active={lang.code === l.code} onClick={() => select(l)} testId={testId} />
              ))}
              <div className="mt-2 border-t border-edge px-3 pt-2 text-center text-[0.65rem] text-ash">
                Powered by AI · dynamically extensible
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

const LangRow = ({ l, active, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={`${testId}-option-${l.code}`}
    className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors duration-200 ${
      active ? "bg-gold/10 text-gold" : "text-cream hover:bg-black/5"
    }`}
  >
    <span className="flex items-center gap-3">
      <span className="font-body">{l.native}</span>
      <span className="text-xs text-ash">{l.name}</span>
    </span>
    {active && <Check className="h-4 w-4" />}
  </button>
);
